import type { ConfigDimensionSegment, ConfigSchema } from './configurator-sku'
import type { CatalogResultCard } from './types'

/**
 * The two, ONLY, real backed facets for the per-series filter sidebar (UID-34
 * / ADR-0099 §5a): Standard/Configurable (`sourceable.kind`) and the union of
 * `range`/`enum` dimension segments the series' rows carry on `configSchema`
 * — the exact same data `ConfiguratorCore` already renders pickers from.
 * Everything else in the fuller mock is deliberately absent from this module —
 * not a disabled stub, omitted entirely (ADR-0090/0091) — but for TWO different
 * reasons, and the difference matters if you are extending this (ADR-0111,
 * which corrects ADR-0099 Gap 1's blanket "zero backing data" claim):
 *
 *   - GENUINELY UNBACKED: End Type / End Shape and End Machining swatches
 *     (absent from all 109 distinct spec names in the seed), and
 *     Stock Availability (a `Stock Availability` spec DOES exist, but it is a
 *     degenerate single-value capture-filter artifact — "Show Products in
 *     Stock" — where absence does NOT mean out-of-stock; real availability
 *     still needs UID-38). Do not build these off `specs`.
 *   - BACKED BUT NOT WIRED HERE: Material (39 values), Finish (28) and
 *     Metric/Inch (2) are REAL per-item facets on `configAttributes.specs`,
 *     carried by ~60% of seeded items. They are omitted from THIS module on
 *     granularity + coverage-asymmetry grounds (ADR-0111 D5), not for want of
 *     data, and they require UID-320b's normalization layer before any
 *     consumer reads them (ADR-0111 D7 — dedup by name, exclude `$L1`/`$L2`/
 *     `$L3` display composites, prefer `kind` over the redundant
 *     `Standard/Configurable` spec).
 */

/** A segment's `fieldName`, or the same "Value" fallback `ConfiguratorCore`
 * uses when the parser stored none — so unlabeled dimensions from different
 * families still group into one sidebar control rather than silently
 * vanishing under an `undefined` key. */
function facetKey(segment: ConfigDimensionSegment): string {
  return segment.fieldName ?? 'Value'
}

/**
 * A row's real value for one dimension column, keyed the same way as
 * {@link deriveSeriesFacets} ({@link facetKey}). `catalog` rows (no
 * `configSchema`) and `configurable` rows that don't carry this particular
 * fieldName both render "—" — the row genuinely has nothing on file for that
 * column, not a fabricated blank (ADR-0090/0091). A `configurable` row is an
 * OPEN family, not one resolved SKU, so a `range` segment's real value is
 * honestly the whole bound (`min`–`max`), and an `enum` segment's real value
 * is its full sorted option list — never a single guessed number.
 */
export function dimensionCellValue(card: CatalogResultCard, fieldName: string): string {
  const schema = card.configSchema as ConfigSchema | undefined
  if (!schema) return '—'
  const segment = schema.segments.find(
    (candidate): candidate is ConfigDimensionSegment =>
      (candidate.kind === 'range' || candidate.kind === 'enum') &&
      facetKey(candidate as ConfigDimensionSegment) === fieldName,
  )
  if (!segment) return '—'
  if (segment.kind === 'range') return `${segment.min}–${segment.max}`
  return [...segment.values].sort((a, b) => a - b).join(', ')
}

export interface SeriesRangeFacet {
  readonly fieldName: string
  readonly kind: 'range'
  readonly min: number
  readonly max: number
}

export interface SeriesEnumFacet {
  readonly fieldName: string
  readonly kind: 'enum'
  readonly values: readonly number[]
}

export type SeriesFacet = SeriesRangeFacet | SeriesEnumFacet

export interface SeriesFacets {
  readonly dimensionFacets: readonly SeriesFacet[]
}

/**
 * Union every `range`/`enum` dimension segment across every row's
 * `configSchema.segments`, keyed by {@link facetKey}. Rows with no
 * `configSchema` (plain `catalog` rows) contribute nothing — their absence is
 * exactly why the Standard/Configurable toggle stays useful even when this
 * returns zero dimension facets. `literal` and `raw` segments are skipped —
 * neither is filterable (`raw` has no legal-value model to bound a filter
 * with, ADR-0098).
 */
export function deriveSeriesFacets(rows: readonly CatalogResultCard[]): SeriesFacets {
  const ranges = new Map<string, { min: number; max: number }>()
  const enums = new Map<string, Set<number>>()
  const order: string[] = []

  function noteOrder(key: string) {
    if (!order.includes(key)) order.push(key)
  }

  for (const row of rows) {
    const schema = row.configSchema as ConfigSchema | undefined
    if (!schema) continue
    for (const segment of schema.segments) {
      if (segment.kind === 'range') {
        const key = facetKey(segment)
        noteOrder(key)
        const existing = ranges.get(key)
        ranges.set(
          key,
          existing
            ? { min: Math.min(existing.min, segment.min), max: Math.max(existing.max, segment.max) }
            : { min: segment.min, max: segment.max },
        )
      } else if (segment.kind === 'enum') {
        const key = facetKey(segment)
        noteOrder(key)
        const existing = enums.get(key) ?? new Set<number>()
        for (const value of segment.values) existing.add(value)
        enums.set(key, existing)
      }
      // 'literal' and 'raw' segments are not filterable — skipped.
    }
  }

  const dimensionFacets: SeriesFacet[] = order.map((fieldName) => {
    const range = ranges.get(fieldName)
    if (range) return { fieldName, kind: 'range', min: range.min, max: range.max }
    const values = enums.get(fieldName)
    return { fieldName, kind: 'enum', values: [...(values ?? [])].sort((a, b) => a - b) }
  })

  return { dimensionFacets }
}

export interface SeriesFilterSelection {
  /** "Standard" checkbox — `sourceable.kind === 'catalog'`. */
  readonly standard: boolean
  /** "Configurable" checkbox — `sourceable.kind === 'configurable'`. */
  readonly configurable: boolean
  /** Active range filters, keyed by {@link facetKey}. A fieldName absent
   * from this map is an INACTIVE filter — every row passes it regardless of
   * whether the row even carries that dimension. Only a present entry gates
   * rows (including excluding rows that lack the fieldName's segment
   * entirely). */
  readonly ranges: ReadonlyMap<string, { readonly min: number; readonly max: number }>
  /** Active enum filters, keyed by {@link facetKey}. Same "absent = inactive"
   * rule as {@link ranges}; a present entry with an empty `Set` is also
   * treated as inactive (matches the mock's "no boxes checked = show all"
   * default — a facet with nothing checked filters nothing). */
  readonly enums: ReadonlyMap<string, ReadonlySet<number>>
}

function passesStandardConfigurableFilter(
  card: CatalogResultCard,
  selection: SeriesFilterSelection,
): boolean {
  if (!selection.standard && !selection.configurable) return true
  if (selection.standard && selection.configurable) return true
  if (selection.standard) return card.sourceable.kind === 'catalog'
  return card.sourceable.kind === 'configurable'
}

function findDimensionSegment(
  card: CatalogResultCard,
  fieldName: string,
  kind: 'range' | 'enum',
): ConfigDimensionSegment | undefined {
  const schema = card.configSchema as ConfigSchema | undefined
  if (!schema) return undefined
  return schema.segments.find(
    (segment): segment is ConfigDimensionSegment =>
      segment.kind === kind && facetKey(segment as ConfigDimensionSegment) === fieldName,
  )
}

function passesDimensionFilters(
  card: CatalogResultCard,
  selection: SeriesFilterSelection,
): boolean {
  for (const [fieldName, { min, max }] of selection.ranges) {
    const segment = findDimensionSegment(card, fieldName, 'range')
    if (!segment || segment.kind !== 'range') return false
    if (segment.max < min || segment.min > max) return false
  }
  for (const [fieldName, values] of selection.enums) {
    if (values.size === 0) continue
    const segment = findDimensionSegment(card, fieldName, 'enum')
    if (!segment || segment.kind !== 'enum') return false
    if (!segment.values.some((value) => values.has(value))) return false
  }
  return true
}

/**
 * Pure predicate over the series' already-fetched rows — no fabricated data,
 * no re-fetch. A row passes iff it matches the Standard/Configurable
 * selection AND, for every ACTIVE per-dimension filter, the row carries a
 * segment for that fieldName whose range overlaps the selected bounds or
 * whose enum values intersect the checked set. A row with no segment for an
 * active filter's fieldName never passes that filter (ADR-0099's
 * catalog/configurable "shape correction" — a `catalog` row and most
 * `configurable` families simply don't carry every dimension the series'
 * OTHER families do).
 */
export function filterSeriesRows(
  rows: readonly CatalogResultCard[],
  selection: SeriesFilterSelection,
): CatalogResultCard[] {
  return rows.filter(
    (card) =>
      passesStandardConfigurableFilter(card, selection) && passesDimensionFilters(card, selection),
  )
}
